import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, getToken } from '../../lib/api';

/**
 * 在线备份（公司管理员）。
 *
 * 范围声明直接来自后端：本页逐系统显示各自最近尝试与最近成功，
 * unsupported 的系统如实显示「未支持」，不把部分成功显示为全部成功。
 * 页面不提供恢复入口，也不接受文件路径或命令参数。
 */

interface Attempt { at: string; status: string; kind: string; stage?: string | null; committed?: boolean; }
interface Success {
  at: string; kind: string; file_name?: string | null; bytes?: number | null;
  sha256?: string | null; verification?: { integrity?: string; reopened?: boolean } | null;
}
interface SystemRow {
  system: string;
  kind: string;
  last_attempt: Attempt | null;
  last_success: Success | null;
  present?: boolean | null;
  bytes?: number | null;
}
interface StatusPayload {
  systems: SystemRow[];
  supported: string[];
  unsupported: string[];
  scope: string;
}

interface RunReport {
  systems: { system: string; status: string; stage?: string; committed?: boolean }[];
  ok: number; failed: number; unsupported: number;
}

const STAGE_HINT: Record<string, string> = {
  E_MUTEX: '互斥未取得',
  E_EXPORT: '内存导出失败',
  E_WRITE_TEMP: '写临时产物失败',
  E_READBACK: '读回失败',
  E_DIGEST: '摘要不符',
  E_REOPEN: '独立重开失败',
  E_INTEGRITY: '完整性检查未通过',
  E_METADATA: '元数据写失败',
  E_COMMIT: '提交失败',
  E_PRUNE: '清理旧备份失败（新备份已在位）',
};

function fmtBytes(n?: number | null): string {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function stageText(code?: string | null): string {
  if (!code) return '';
  const key = String(code).replace(/^CONTRACT_|^HTTP_/, '');
  return STAGE_HINT[key] ? `${key}（${STAGE_HINT[key]}）` : key;
}

export default function SystemBackupsPage() {
  const [rows, setRows] = useState<SystemRow[]>([]);
  const [scope, setScope] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [activeJob, setActiveJob] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ data: StatusPayload; active_job: string | null }>('/api/admin/system-backups/status');
      setRows(r.data.systems || []);
      setScope(r.data.scope || '');
      setActiveJob(r.active_job || null);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '无法读取备份状态');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // poll while a run is in flight so the button reflects the real job state
  useEffect(() => {
    if (!running && !activeJob) {
      if (timer.current) { window.clearInterval(timer.current); timer.current = null; }
      return;
    }
    timer.current = window.setInterval(() => { void load(); }, 2000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [running, activeJob, load]);

  async function waitForJob(id: string): Promise<string> {
    for (let i = 0; i < 180; i += 1) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const r = await api.get<{ data: { running: boolean; result: RunReport | null } }>(
          `/api/admin/system-backups/jobs/${id}`);
        if (!r.data.running) {
          const res = r.data.result;
          if (!res) return '任务结束但未返回结果';
          const failed = res.systems.filter((s) => s.status === 'failed');
          const unsupported = res.systems.filter((s) => s.status === 'unsupported');
          const parts = [`成功 ${res.ok}/${res.systems.length}`];
          if (failed.length) {
            parts.push(`失败 ${failed.map((s) => `${s.system} ${stageText(s.stage) || '请查看记录'}${s.committed ? '（新备份已提交，后续失败）' : ''}`).join('、')}`);
          }
          if (unsupported.length) parts.push(`未支持 ${unsupported.map((s) => s.system).join('、')}`);
          return parts.join(' · ');
        }
      } catch { /* keep polling */ }
    }
    return '任务仍在执行，请稍后刷新';
  }

  async function runNow() {
    setRunning(true);
    setNotice(null);
    try {
      const r = await api.post<{ job_id: string }>('/api/admin/system-backups/run', {});
      setNotice(`已启动任务 ${r.job_id}`);
      setNotice(await waitForJob(r.job_id));
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : '启动备份失败');
    } finally {
      setRunning(false);
      await load();
    }
  }

  async function download(system: string) {
    try {
      const response = await fetch(`/api/admin/system-backups/${encodeURIComponent(system)}/download`, {
        headers: { authorization: `Bearer ${getToken()}` },
      });
      if (!response.ok) throw new Error(`下载失败 (${response.status})`);
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url; link.download = `${system}.db`; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setNotice(e instanceof Error ? e.message : '下载失败'); }
  }

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>在线备份</h2>
        <button onClick={runNow} disabled={running || !!activeJob}>{running || activeJob ? '执行中…' : '立即备份'}</button>
        <button onClick={() => void load()}>刷新</button>
      </header>

      <p style={{ margin: 0, fontSize: 12, opacity: 0.7 }}>
        {scope || '逐系统独立备份；不宣称跨系统共同恢复点。'}
      </p>

      {error && <div style={{ color: '#c0392b' }}>{error}</div>}
      {notice && <div style={{ padding: 8, background: '#f4f6f8', borderRadius: 4 }}>{notice}</div>}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
            <th style={{ padding: 6 }}>系统</th>
            <th style={{ padding: 6 }}>最近尝试</th>
            <th style={{ padding: 6 }}>最近成功</th>
            <th style={{ padding: 6 }}>核验</th>
            <th style={{ padding: 6 }}>大小</th>
            <th style={{ padding: 6 }}>下载</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const supported = s.kind === 'sqlite' || s.kind === 'qc';
            const attemptFailed = s.last_attempt?.status === 'failed';
            return (
              <tr key={s.system} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: 6 }}>{s.system}</td>
                <td style={{ padding: 6 }}>
                  {!s.last_attempt ? '—'
                    : attemptFailed
                      ? <span style={{ color: '#c0392b' }}>失败 · {stageText(s.last_attempt.stage) || '请查看记录'}</span>
                      : `${s.last_attempt.status} · ${s.last_attempt.at}`}
                </td>
                <td style={{ padding: 6 }}>
                  {!supported ? <span style={{ opacity: 0.6 }}>未支持</span>
                    : s.last_success ? `${s.last_success.at} · ${s.last_success.file_name ?? ''}`
                      : '—'}
                </td>
                <td style={{ padding: 6 }}>
                  {s.last_success?.verification?.integrity === 'ok' ? '完整性 ok' : '—'}
                </td>
                <td style={{ padding: 6 }}>
                  {attemptFailed && s.last_success
                    ? <span title="最近成功记录；本次失败后的文件状态需按阶段核对">{fmtBytes(s.last_success.bytes)}（最近成功）</span>
                    : fmtBytes(s.last_success?.bytes)}
                </td>
                <td style={{ padding: 6 }}>
                  {s.kind === 'sqlite' && s.present
                    ? <button onClick={() => { void download(s.system); }}>下载</button>
                    : <span style={{ opacity: 0.5 }}>—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p style={{ margin: 0, fontSize: 12, opacity: 0.7 }}>
        恢复为独立操作，本页不提供恢复入口。每次仅保留一个已核验成功的数据备份，无法回到更早日期。
      </p>
    </div>
  );
}
